import { FormSection } from "Components/Shared/FormSection";
import { useIdentificationCardList } from "Hooks/Options/useIdentificationCardList";
import { useFormatLocale } from "Hooks/useFormatLocale";
import { isValid } from "date-fns";
import { Resources, useResource } from "Translations/Resources";
import type { PartyIdentificationDto } from "Infrastructure/Api/Api";
import { Button, Stack } from "@mui/material";
import { ExpirationAlert } from "Components/Shared/ExpirationAlert";
import { DataRow } from "Components/Shared/DataRow/DataRow";
import { useState } from "react";
import { getDocumentExpirationStatus } from "Utils/DocumentExpiration/documentExpirationUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUploadIdentityDocuments } from "Api/Mutations/Onboarding/useUploadIdentityDocuments";
import {
  getDocumentsValidationSchema,
  type DocumentsUploadFormData,
} from "Components/Onboarding/shared/DocumentsUpload/DocumentsUploadForm/schema";
import { IdentityCardForms } from "Components/Onboarding/shared/IdentityCardForms";
import { ProcessingOcr } from "Components/Onboarding/shared/ProcessingOcr";
import { useForm } from "react-hook-form";
import { SecondDocumentUpload } from "Components/UserAccount/Shared/SecondDocumentUpload";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form;
const IDENTIFICATION_RESOURCES = COMPONENT_RESOURCES.PartyIdentification;
const EXPIRATION_RESOURCES = Resources.Components.DocumentExpirationWarning;
const ACCOUNT_RESOURCES = Resources.Components.Account;
const COMMON_RESOURCES = Resources.Common;

type Props = {
  partyIdentification: PartyIdentificationDto;
  partyPublicID?: string;
  isDocumentExpired?: boolean;
  isSecondDocumentRequired?: boolean;
};

export const DocumentsDisplay = ({
  partyIdentification: {
    identification,
    identificationNumber,
    identificationValidFrom,
    identificationValidTo = null,
    identificationIssuer,
    secondIdentificationValidTo = null,
    secondIdentification,
    secondIdentificationNumber,
    secondIdentificationValidFrom,
    secondIdentificationIssuer,
  },
  partyPublicID,
  isDocumentExpired,
  isSecondDocumentRequired,
}: Props) => {
  const { identificationCards } = useIdentificationCardList();
  const format = useFormatLocale();
  const { t } = useResource();
  const [isFirstUploadVisible, setIsFirstUploadVisible] = useState(false);
  const { mutate: uploadDocuments, isPending: isUploading } =
    useUploadIdentityDocuments({
      successMessage: t(EXPIRATION_RESOURCES.UploadSuccess),
      onSuccess: () => setIsFirstUploadVisible(false),
    });

  const firstDocSchema = getDocumentsValidationSchema(t, false);
  const { control: firstDocControl, handleSubmit: handleFirstDocSubmit } =
    useForm<DocumentsUploadFormData>({
      resolver: zodResolver(firstDocSchema),
    });

  const onFirstDocSubmit = (data: DocumentsUploadFormData) => {
    if (!partyPublicID) {
      return;
    }
    uploadDocuments({
      documents: {
        firstIDCardBackSide: data.firstDocument.backSide,
        firstIDCardFrontSide: data.firstDocument.frontSide,
      },
      partyPublicID,
    });
  };

  const documentType = identification
    ? identificationCards.find(({ code }) => code === identification)?.name ||
      "-"
    : "-";

  const validFrom =
    identificationValidFrom && new Date(identificationValidFrom);
  const formattedValidFrom =
    validFrom && isValid(validFrom) ? format(validFrom) : "-";

  const validTo = identificationValidTo && new Date(identificationValidTo);
  const formattedValidTo = validTo && isValid(validTo) ? format(validTo) : "-";

  const primary = getDocumentExpirationStatus(
    identificationValidTo,
    isDocumentExpired,
  );
  const secondary = getDocumentExpirationStatus(secondIdentificationValidTo);

  const hasSecondDocument = !!secondIdentification;

  const secondDocumentType = secondIdentification
    ? identificationCards.find(({ code }) => code === secondIdentification)
        ?.name || "-"
    : "-";

  const secondValidFrom =
    secondIdentificationValidFrom && new Date(secondIdentificationValidFrom);
  const formattedSecondValidFrom =
    secondValidFrom && isValid(secondValidFrom) ? format(secondValidFrom) : "-";

  const secondValidTo =
    secondIdentificationValidTo && new Date(secondIdentificationValidTo);
  const formattedSecondValidTo =
    secondValidTo && isValid(secondValidTo) ? format(secondValidTo) : "-";

  return (
    <Stack gap={3}>
      <FormSection
        legend={t(COMPONENT_RESOURCES.NaturalPerson.Legend.Documents)}
        noMargins
      >
        <Stack gap={2}>
          {primary.isExpired && (
            <ExpirationAlert
              isExpired
              title={t(EXPIRATION_RESOURCES.ExpiredPrimaryTitle)}
              message={t(EXPIRATION_RESOURCES.ExpiredPrimaryMessage)}
            />
          )}
          {primary.isExpiringSoon && (
            <ExpirationAlert
              isExpired={false}
              title={t(EXPIRATION_RESOURCES.WarningPrimaryTitle)}
              message={t(EXPIRATION_RESOURCES.WarningPrimaryMessage, {
                date: formattedValidTo,
              })}
            />
          )}
          {hasSecondDocument && secondary.isExpired && (
            <ExpirationAlert
              isExpired
              title={t(EXPIRATION_RESOURCES.ExpiredSecondaryTitle)}
              message={t(EXPIRATION_RESOURCES.ExpiredSecondaryMessage)}
            />
          )}
          {hasSecondDocument && secondary.isExpiringSoon && (
            <ExpirationAlert
              isExpired={false}
              title={t(EXPIRATION_RESOURCES.WarningSecondaryTitle)}
              message={t(EXPIRATION_RESOURCES.WarningSecondaryMessage, {
                date:
                  secondIdentificationValidTo &&
                  isValid(new Date(secondIdentificationValidTo))
                    ? format(new Date(secondIdentificationValidTo))
                    : "-",
              })}
            />
          )}
          <DataRow
            label={t(IDENTIFICATION_RESOURCES.Identification)}
            value={documentType}
          />
          <DataRow
            label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
            value={identificationNumber || null}
          />
          <DataRow
            label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
            value={formattedValidFrom}
          />
          <DataRow
            label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
            value={formattedValidTo}
          />
          <DataRow
            label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
            value={identificationIssuer || null}
          />
          {partyPublicID && (
            <>
              {!isFirstUploadVisible && (
                <Button
                  variant="outlined"
                  color={primary.isExpired ? "error" : "primary"}
                  onClick={() => {
                    setIsFirstUploadVisible(true);
                  }}
                >
                  {t(EXPIRATION_RESOURCES.UploadNewDocument)}
                </Button>
              )}
              {isFirstUploadVisible &&
                (isUploading ? (
                  <ProcessingOcr />
                ) : (
                  <form onSubmit={handleFirstDocSubmit(onFirstDocSubmit)}>
                    <Stack gap={2}>
                      <IdentityCardForms
                        control={firstDocControl}
                        frontSideName="firstDocument.frontSide"
                        backSideName="firstDocument.backSide"
                      />
                      <Button type="submit" variant="contained">
                        {t(EXPIRATION_RESOURCES.UploadNewDocument)}
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => {
                          setIsFirstUploadVisible(false);
                        }}
                      >
                        {t(COMMON_RESOURCES.Cancel)}
                      </Button>
                    </Stack>
                  </form>
                ))}
            </>
          )}
        </Stack>
      </FormSection>

      {isSecondDocumentRequired && (
        <FormSection
          legend={t(ACCOUNT_RESOURCES.SecondDocumentUpload.Title)}
          noMargins
        >
          <Stack gap={2}>
            {hasSecondDocument && (
              <>
                <DataRow
                  label={t(IDENTIFICATION_RESOURCES.Identification)}
                  value={secondDocumentType}
                />
                <DataRow
                  label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
                  value={secondIdentificationNumber || null}
                />
                <DataRow
                  label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
                  value={formattedSecondValidFrom}
                />
                <DataRow
                  label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
                  value={formattedSecondValidTo}
                />
                <DataRow
                  label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
                  value={secondIdentificationIssuer || null}
                />
              </>
            )}
            {partyPublicID && (
              <SecondDocumentUpload
                partyPublicID={partyPublicID}
                isExpired={secondary.isExpired}
              />
            )}
          </Stack>
        </FormSection>
      )}
    </Stack>
  );
};
